import api, { ApiResponse } from './api';

export interface DashboardMetrics {
  impressions: number;
  clicks: number;
  spend: number;
  reach: number;
  frequency: number;
  ctr: number;
  cpc: number;
  cpm: number;
  conversions: number;
  conversion_rate: number;
  roas: number;
}

export interface TimeSeriesData {
  date: string;
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
}

export interface BreakdownData {
  dimension: string;
  impressions: number;
  clicks: number;
  spend: number;
  ctr: number;
}

export interface Insight {
  id: string;
  date: string;
  campaign_id?: string;
  adset_id?: string;
  ad_id?: string;
  impressions: number;
  clicks: number;
  spend: number;
  reach: number;
  frequency: number;
  ctr: number;
  cpc: number;
  cpm: number;
  conversions: number;
  cpa: number;
  roas: number;
}

export interface DashboardRow {
  entity_id: string;
  name: string;
  status: string;
  /** What the platform is actually doing; null only for a genuine unpublished draft. */
  effective_status: string | null;
  /** A parent changed and the platform has not confirmed what that did here yet. */
  status_stale_since: string | null;
  /** Ad level only; null at campaign and ad set, which have no single creative. */
  creative_image_url: string | null;
  creative_video_url: string | null;
  headline: string | null;
  daily_budget: number | null;
  spend: number;
  revenue: number;
  profit: number;
  roi: number;
  roi_yesterday: number | null;
  roi_2d_ago: number | null;
  impressions: number;
  clicks: number;
  ctr: number;
  cpc: number;
  conversions: number;
  cpa: number;
  roas: number;
}

export interface DashboardTotals {
  total_row_count: number;
  spend: number;
  revenue: number;
  profit: number;
  impressions: number;
  clicks: number;
  conversions: number;
}

export interface DashboardPage {
  rows: DashboardRow[];
  totals: DashboardTotals;
}

export interface DashboardParams {
  ad_account_id?: string;
  date_from: string;
  date_to: string;
  level: 'campaign' | 'adset' | 'ad';
  campaign_ids?: string[];
  adset_ids?: string[];
  include_archived?: boolean;
  limit?: number;
  offset?: number;
  order_by?: string;
  order?: 'asc' | 'desc';
}

export interface InsightsParams {
  ad_account_id?: string;
  campaign_id?: string;
  adset_id?: string;
  ad_id?: string;
  date_from: string;
  date_to: string;
}

export const insightsService = {
  getDashboard: async (params: DashboardParams): Promise<DashboardPage> => {
    const response = await api.post<ApiResponse<DashboardPage>>('/insights/dashboard/query', params);
    return response.data.data ?? { rows: [], totals: { total_row_count: 0, spend: 0, revenue: 0, profit: 0, impressions: 0, clicks: 0, conversions: 0 } };
  },

  getOverview: async (params: InsightsParams): Promise<DashboardMetrics> => {
    const response = await api.get<ApiResponse<DashboardMetrics>>('/insights/overview', {
      params,
    });
    return response.data.data!;
  },

  getTimeSeries: async (params: InsightsParams): Promise<TimeSeriesData[]> => {
    const response = await api.get<ApiResponse<TimeSeriesData[]>>('/insights/time-series', {
      params,
    });
    return response.data.data!;
  },

  getBreakdown: async (
    params: InsightsParams & { breakdown: 'age' | 'gender' | 'placement' | 'device' }
  ): Promise<BreakdownData[]> => {
    const response = await api.get<ApiResponse<BreakdownData[]>>('/insights/breakdown', {
      params,
    });
    return response.data.data!;
  },

  getCampaignInsights: async (
    campaignId: string,
    dateRange: { date_from: string; date_to: string }
  ): Promise<Insight[]> => {
    const response = await api.get<ApiResponse<Insight[]>>('/insights/campaign', {
      params: {
        campaign_id: campaignId,
        ...dateRange,
      },
    });
    return response.data.data || [];
  },

  getAdSetInsights: async (
    adsetId: string,
    dateRange: { date_from: string; date_to: string }
  ): Promise<Insight[]> => {
    const response = await api.get<ApiResponse<Insight[]>>('/insights/adset', {
      params: {
        adset_id: adsetId,
        ...dateRange,
      },
    });
    return response.data.data || [];
  },

  getAdInsights: async (
    adId: string,
    dateRange: { date_from: string; date_to: string }
  ): Promise<Insight[]> => {
    const response = await api.get<ApiResponse<Insight[]>>('/insights/ad', {
      params: {
        ad_id: adId,
        ...dateRange,
      },
    });
    return response.data.data || [];
  },
};
